import { useState, useCallback, useEffect, useRef } from 'react';
import { debounce } from 'lodash';
import { decrypt } from '../xor';
import { format, getDay } from 'date-fns';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Cookies
const getCookie = (name) => {
  const m = document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)');
  return m ? decodeURIComponent(m.pop()) : null;
};

// Utility to create date range
const mkRange = (from, to) => {
  const arr = [];
  const cur = new Date(from.setHours(0, 0, 0, 0));
  const end = new Date(to.setHours(0, 0, 0, 0));
  while (cur <= end) {
    arr.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return arr;
};

// Map day index (0=Sunday, 1=Monday, etc.) to day name
const dayIndexToName = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

export function useTimesheetDetail(entity_type, entity_id) {
  // Default filter: current month
  const [filterStart, setFilterStart] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });
  const [filterEnd, setFilterEnd] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1, 0);
    return d;
  });

  const [dateRange, setDateRange] = useState(() =>
    mkRange(new Date(filterStart), new Date(filterEnd))
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [detail, setDetail] = useState(null);

  // Prevent duplicate fetches
  const isFetching = useRef(false);

  // Resolve start_time for each employee per date
  const resolveStartTime = (member, date) => {
    const dayOfWeek = dayIndexToName[getDay(date)];
    let startTime = null;

    // Priority 1: Employee start_time
    if (member.start_time) {
      startTime = member.start_time;
    }

    // Priority 2: Team schedules
    if (!startTime && member.team_schedules) {
      for (const sched of member.team_schedules) {
        if (sched.days_of_week.split(',').includes(dayOfWeek)) {
          startTime = sched.start_times.split(',')[0];
          break;
        }
      }
    }

    // Priority 3: Department schedules
    if (!startTime && member.department_schedules) {
      for (const sched of member.department_schedules) {
        if (sched.days_of_week.split(',').includes(dayOfWeek)) {
          startTime = sched.start_times.split(',')[0];
          break;
        }
      }
    }

    // Priority 4: Organization schedules
    if (!startTime && member.organization_schedules) {
      for (const sched of member.organization_schedules) {
        if (sched.days_of_week.split(',').includes(dayOfWeek)) {
          startTime = sched.start_times.split(',')[0];
          break;
        }
      }
    }

    return startTime || '09:30';
  };

  // Debounced fetch to limit request rate
  const debouncedFetch = useCallback(
    debounce(async (from, to, reqId, department_id, team_id, currentDateRange) => {
      if (isFetching.current) {
        console.log('Fetch skipped: already in progress');
        return;
      }
      isFetching.current = true;
      setLoading(true);
      setErr(null);

      try {
        const qs = new URLSearchParams({
          auth_token: localStorage.getItem('auth_token') || '',
          entity_type,
          entity_id,
          start_date: format(from, 'yyyy-MM-dd'),
          filter_end_date: format(to, 'yyyy-MM-dd'),
        });
        if (department_id) qs.append('department_id', department_id);
        if (team_id) qs.append('team_id', team_id);

        // 🔑 Hide deactivated by default: if cookie != "1", force only active
        const showInactive = getCookie('show_inactive_users') === '1';
        if (!showInactive) {
          qs.append('is_active', 'true');
        }

        console.log(`Fetching timesheet: ${qs.toString()}`);
        const res = await fetch(`${API_URL}/admin/entity_timesheet_detail?${qs}`);
        const j = await res.json();

        if (j.status !== 'success') {
          setErr(j.message || 'Server error');
        } else {
          const plain = decrypt(j.data);
          const jsonString = plain.slice(0, plain.lastIndexOf('}') + 1);

          try {
            const parsedData = JSON.parse(jsonString);
            parsedData.members = parsedData.members.map((member) => ({
              ...member,
              resolvedStartTimes: currentDateRange.reduce((acc, date) => {
                const iso = format(date, 'yyyy-MM-dd');
                acc[iso] = resolveStartTime(member, date);
                return acc;
              }, {}),
            }));
            setData(parsedData);
          } catch {
            setErr('Bad JSON in decrypted data');
          }
        }
      } catch (e) {
        console.error('Fetch error:', e);
        setErr('Network or unexpected error.');
      } finally {
        setLoading(false);
        isFetching.current = false;
      }
    }, 600),
    [entity_type, entity_id]
  );

  const fetchData = useCallback(
    (from, to, reqId, department_id, team_id) => {
      const currentDateRange = mkRange(new Date(from), new Date(to));
      debouncedFetch(from, to, reqId, department_id, team_id, currentDateRange);
    },
    [debouncedFetch]
  );

  useEffect(() => {
    const newDateRange = mkRange(new Date(filterStart), new Date(filterEnd));
    setDateRange(newDateRange);
    fetchData(filterStart, filterEnd, 0, null, null);
    return () => debouncedFetch.cancel();
  }, [filterStart, filterEnd, fetchData]);

  return {
    filterStart,
    setFilterStart,
    filterEnd,
    setFilterEnd,
    dateRange,
    setDateRange,
    data,
    loading,
    err,
    setErr,
    detail,
    setDetail,
    fetchData,
  };
}
